'use client';

import type { ReactElement } from 'react';
import { useSyncExternalStore } from 'react';
import { useSearchParams } from 'next/navigation';
import { Content } from '@packages/cms-components/src/components/CmsContent/Content';
import type { CmsData } from '@packages/cms-components/interfaces';
import { filteredContent } from '@packages/cms-components/src/utils/filterParagraphs';
import {
  getAllAudiences,
  getEcStateFromParams,
} from '@packages/cms-components/src/utils/audiences';
import { previewSettingsStore, previewSettingsServerSnapshot } from './previewSettingsStore';
import { DummyMenu, DummyProducts } from './PreviewDummy';
import { ContentWidth } from '../CmsContent/ContentWidth';

const LayoutWithMenu = ({ menu, content }: { menu?: ReactElement; content: ReactElement }) => (
  <div className="my-4 grid auto-cols-[1fr] grid-flow-col gap-1">
    {menu && <div className="col-span-1 max-md:hidden">{menu}</div>}
    <div className="col-span-3 lg:col-span-4">{content}</div>
  </div>
);

export const Preview = ({
  content,
  token,
  hasRealMenuData,
  noProducts,
}: {
  content: CmsData;
  token: string | null;
  /** indicates that a dummy menu is obsolete, because a real menu exists */
  hasRealMenuData?: boolean;
  /** indicates that dummy products are obsolete, because real products exist */
  noProducts?: boolean;
}) => {
  const settings = useSyncExternalStore(
    previewSettingsStore.subscribe,
    previewSettingsStore.get,
    previewSettingsServerSnapshot,
  );

  const audiences = getAllAudiences(getEcStateFromParams(settings.audiences ?? ''));
  const testDate = settings.testDate ?? '';
  const showAll = settings.showAll === 'true';

  const hasServiceMenu = content.menu === 'service' && content.menuFull;

  const query = Object.fromEntries(useSearchParams()?.entries() ?? []);
  const { position: productsPosition = 'NoProducts', leftnavi = false } = query;

  const contentData = showAll ? content : filteredContent(content, testDate, audiences);

  const children = (
    <Content
      data={contentData}
      menuData={hasServiceMenu ? content.menuFull : undefined}
      token={token ?? undefined}
    />
  );

  const showWithDummyProducts = productsPosition !== 'NoProducts' && !noProducts;
  if (showWithDummyProducts) {
    return (
      <ContentWidth contentWidth={contentData.field_maximum_content_width}>
        {productsPosition === 'Top' && children}
        <LayoutWithMenu
          menu={leftnavi === 'true' && !hasRealMenuData ? <DummyMenu /> : undefined}
          content={<DummyProducts />}
        />
        {productsPosition === 'Bottom' && children}
      </ContentWidth>
    );
  }

  const showWithDummyMenu = leftnavi === 'true' && !hasServiceMenu && !hasRealMenuData;
  if (showWithDummyMenu) {
    return (
      <ContentWidth contentWidth={contentData.field_maximum_content_width}>
        <div className="grid auto-cols-[1fr] grid-flow-col gap-1">
          <div className="col-span-1 max-md:hidden">
            <DummyMenu />
          </div>
          <div className="col-span-3 lg:col-span-4">{children}</div>
        </div>
      </ContentWidth>
    );
  }

  return children;
};
